// DEPENDENCIES ---------------------------------------------------------------- //

import { gql, useQuery } from '@apollo/client';

import { getMonetaryValue } from '@services/formatService';

import { LEDGER_TYPE, resolveProgramVisuals, type ProgramVisualsConfig } from '@features/programs/programVisuals';

import { css } from '@emotion/css';
import { Paper, Theme, Typography, useTheme } from '@mui/material';

import Spacer from '@components/Utils/Spacer';

// GQL ---------------------------------------------------------------- //

// Informational read of the program money ledger for this invoice's moves /
// scenarios. Row access is scoped to the dealer's own customer by the Hasura
// dealer-role permission on program_ledger.
const GET_PROGRAM_LEDGER_FOR_INVOICE = gql`
  query getProgramLedgerForInvoice($where: program_ledger_bool_exp!) {
    program_ledger(where: $where) {
      id
      move_id
      scenario_id
      type
      estimated_amount
      actual_amount
      confirmed
      program { id name }
    }
  }
`;

// TYPES ---------------------------------------------------------------- //

type LedgerRow = {
  id: number;
  type: string;
  estimated_amount?: number | string | null;
  actual_amount?: number | string | null;
  program?: { id?: number; name?: string | null } | null;
};

type InvoiceProgramBreakdownProps = {
  /** The invoice customer's `effective_config`, where a program-level flag shows up. */
  effectiveConfig?: object | null;
  moveIds?: Array<number | null | undefined>;
  scenarioIds?: Array<number | null | undefined>;
};

// HELPERS ---------------------------------------------------------------- //
// Exported for unit tests — the money math + flag gating live here; the
// component is a thin renderer over them.

// Confirmed actuals win over estimates per row (Hasura numerics arrive as strings).
const sumLedgerType = (rows: LedgerRow[], type: string): number =>
  rows
    .filter(row => row.type === type)
    .reduce((total, row) => {
      const amount = row.actual_amount ?? row.estimated_amount;
      return total + (amount == null ? 0 : Number(amount));
    }, 0);

export type ProgramBreakdownLines = {
  covered: number;
  reimbursement: number;
  recoup: number;
  showCoveredLine: boolean;
  showReimbursementLine: boolean;
  showRecoupLine: boolean;
};

/**
 * The informational lines to show for an invoice's program ledger rows, or
 * null when nothing should render (visuals off, no rows, or no line to show).
 * `show_recoup` additionally gates the "you owe program" line.
 *
 * This maps the PERSISTED program_ledger. Its live, create-time counterpart —
 * driven by a `quoteProgram` result before any ledger exists — is
 * `buildProgramCoverageLines` in `features/moveCreation/lib/programCoverageLines`;
 * keep the two in step when changing what the dealer sees for program coverage.
 */
export const buildProgramBreakdown = (
  visuals: ProgramVisualsConfig,
  rows: LedgerRow[],
): ProgramBreakdownLines | null => {
  if (visuals.active !== true || rows.length === 0) return null;

  const lines: ProgramBreakdownLines = {
    covered: sumLedgerType(rows, LEDGER_TYPE.PROGRAM_PAYMENT),
    reimbursement: sumLedgerType(rows, LEDGER_TYPE.REIMBURSEMENT),
    recoup: sumLedgerType(rows, LEDGER_TYPE.RECOUP),
    showCoveredLine: rows.some(row => row.type === LEDGER_TYPE.PROGRAM_PAYMENT),
    showReimbursementLine: rows.some(row => row.type === LEDGER_TYPE.REIMBURSEMENT),
    showRecoupLine: visuals.show_recoup === true && rows.some(row => row.type === LEDGER_TYPE.RECOUP),
  };

  if (!lines.showCoveredLine && !lines.showReimbursementLine && !lines.showRecoupLine) return null;
  return lines;
};

// COMPONENT ---------------------------------------------------------------- //

/**
 * Informational program money breakdown for an invoice — what the program
 * covered, what the dealer can expect back as a reimbursement, and (when
 * `show_recoup` is on) what the dealer owes the program back. Display only:
 * it never alters the invoice's real AR totals, and renders nothing unless
 * the `program_visuals` flag on the customer's merged config is on and program money exists for
 * the invoice's moves.
 */
const InvoiceProgramBreakdown = ({
  effectiveConfig,
  moveIds,
  scenarioIds,
}: InvoiceProgramBreakdownProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const visuals = resolveProgramVisuals(effectiveConfig);

  const cleanMoveIds = (moveIds ?? []).filter((id): id is number => typeof id === 'number');
  const cleanScenarioIds = (scenarioIds ?? []).filter((id): id is number => typeof id === 'number');

  const orConditions: Array<Record<string, { _in: number[] }>> = [];
  if (cleanMoveIds.length) orConditions.push({ move_id: { _in: cleanMoveIds } });
  if (cleanScenarioIds.length) orConditions.push({ scenario_id: { _in: cleanScenarioIds } });

  const { data } = useQuery(GET_PROGRAM_LEDGER_FOR_INVOICE, {
    variables: { where: { _or: orConditions } },
    skip: visuals.active !== true || orConditions.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const rows: LedgerRow[] = data?.program_ledger ?? [];
  const lines = buildProgramBreakdown(visuals, rows);
  if (!lines) return null;

  const { covered, reimbursement, recoup, showCoveredLine, showReimbursementLine, showRecoupLine } = lines;
  // A customer is in one program, so the rows share it — use its name.
  const program = rows.find(r => r.program?.name)?.program?.name || 'the program';

  return (
    <div className={cls.breakdown}>
      <Paper variant='custom' sx={{ padding: `16px` }}>
        <Typography variant='h5' sx={{ lineHeight: 1, fontWeight: 600 }}>
          Program Coverage
        </Typography>

        <Typography
          variant='body2'
          sx={{ marginTop: `4px`, lineHeight: 1, fontSize: `12px`, color: theme?.palette?.text?.secondary }}
        >
          * Informational — does not change your invoice total
        </Typography>

        <Spacer />

        {showCoveredLine ? (
          <div className={cls.flex}>
            <Typography sx={clsx.keyText}>Covered by {program}</Typography>
            <Typography sx={clsx.valText}>{getMonetaryValue(covered)}</Typography>
          </div>
        ) : null}

        {showReimbursementLine ? (
          <div className={cls.flex}>
            <Typography sx={clsx.keyText}>Expected reimbursement from {program}</Typography>
            <Typography sx={clsx.valText}>{getMonetaryValue(reimbursement)}</Typography>
          </div>
        ) : null}

        {showRecoupLine ? (
          <div className={cls.flex}>
            <Typography sx={clsx.keyText}>Expected payment to {program}</Typography>
            <Typography sx={clsx.valText}>{getMonetaryValue(recoup)}</Typography>
          </div>
        ) : null}
      </Paper>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (_theme?: Theme) => ({
  breakdown: css`
    position: relative;
    margin-top: 16px;
  `,

  flex: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
  `,
});

const useSxStyles = (theme?: Theme) => ({
  keyText: {
    lineHeight: 1.5,
    fontSize: `14px`,
    color: theme?.palette?.text?.secondary,
  },
  valText: {
    lineHeight: 1.5,
    fontSize: `14px`,
    fontWeight: 600,
    color: theme?.palette?.text?.primary,
  },
});

// EXPORT ---------------------------------------------------------------- //

export default InvoiceProgramBreakdown;
